import { useId } from 'react'
import s from './AnimatedEehaeWordmark.module.css'

/**
 * Animated lowercase ``eehae`` wordmark.
 *
 * Composes five letterforms (e, e, h, a, e) as inline ``<svg>``
 * siblings inside a flex container. Each letter draws itself by
 * masking a filled letterform with a stroke that animates from
 * ``stroke-dashoffset: <length>`` to 0 — the filled letterform
 * underneath is revealed progressively as the stroke's dashoffset
 * animates to zero.
 *
 * Letter geometry is taken verbatim from the standalone artboards
 * at ``V3/frontend/src/assets/eehae-{e,h,a}-animated.{html,svg}``.
 * Each letter renders inside its own ``<svg>`` with the artboard's
 * original viewBox + transform stack, so the rendered glyphs match
 * the source designs exactly.
 *
 * Layout:
 *
 *   - Parent flex container with ``align-items: flex-end`` so the
 *     three glyph types align along the bottom of the wordmark.
 *   - ``h`` has a taller viewBox (300 units) because it captures the
 *     ascender; ``e`` and ``a`` are 230 units tall. The CSS sizes
 *     ``h`` at 100% of the wordmark's height and ``e`` / ``a`` at
 *     230/300 ≈ 76.7% so the rendered proportions match the source.
 *   - All five strokes start at t=0 and finish by t=1.9s — no
 *     stagger. Per-letter delay knobs are easy to add later via the
 *     ``--animated-mark-delay`` CSS custom property.
 *
 * A11y: the wrapper carries ``role="img" aria-label="eehae"``; each
 * letter SVG is ``aria-hidden`` so screen readers announce the
 * wordmark as one unit instead of "e e h a e".
 *
 * Customization knobs (all optional CSS custom properties):
 *   - ``--animated-mark-color``     (default ``#fff``)  letter fill
 *   - ``--animated-mark-duration``  (default ``1.9s``)  reveal length
 *   - ``--animated-mark-delay``     (default ``0.1s``)  reveal delay
 *
 * Reduced motion: ``prefers-reduced-motion: reduce`` skips the reveal
 * entirely and shows the fully-rendered wordmark immediately. Matches
 * the policy in ``splash-chrome.css`` for the text logo it replaces.
 */
export default function AnimatedEehaeWordmark({
  className,
}: {
  className?: string
}) {
  // useId() namespaces mask ids so multiple wordmark instances on
  // the same page (e.g. splash + footer) don't collide on the SVG
  // document-global id space. React's useId() returns stable
  // hydration-safe ids. Each letter gets a distinct mask id; the
  // three ``e``s each get their own so the same mask isn't shared
  // (sharing would work but pinning per-instance keeps the SVG
  // tree symmetric and avoids surprising debugging if the mask is
  // ever varied per-letter later).
  const reactId = useId()
  return (
    <span
      role="img"
      aria-label="eehae"
      className={[s.wordmark, className].filter(Boolean).join(' ')}
    >
      <LetterE maskId={`eehae-e1-${reactId}`} />
      <LetterE maskId={`eehae-e2-${reactId}`} />
      <LetterH maskId={`eehae-h-${reactId}`} />
      <LetterA maskId={`eehae-a-${reactId}`} />
      <LetterE maskId={`eehae-e3-${reactId}`} />
    </span>
  )
}

/* ── Letterforms ───────────────────────────────────────────────────── *
 * Each letter sub-component is a self-contained <svg> lifted verbatim
 * from its source artboard. The mask path drives the stroke reveal;
 * the filled letterform underneath is what's actually visible. The
 * three letters share an outer transform stack (matrix(...) →
 * matrix(1.1748...)) because they were exported from a single source
 * SVG; that stack is preserved unchanged so the rendered glyph shapes
 * match the artboards.
 * ─────────────────────────────────────────────────────────────────── */

function LetterE({ maskId }: { maskId: string }) {
  return (
    <svg
      viewBox="60 150 230 230"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={s.letterE}
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="60"
          y="150"
          width="230"
          height="230"
        >
          <rect x="60" y="150" width="230" height="230" fill="black" />
          <g transform="translate(38.85, 69.5) scale(0.79)">
            <path
              className={s.strokeE}
              d="M 141.4 244.8 C 165 244, 225 244, 268 244 C 268 216, 251.1 189.6, 235.1 173.2 C 216.6 150.5, 175 138, 118.7 165.9 C 94.2 192.3, 79.7 214.4, 81.1 245.2 C 80.3 275, 97.3 307.2, 124.3 323.6 C 155.1 340.6, 186.9 341.6, 216.6 335.6 C 240 325.5, 255.6 316.4, 263 305 C 268 295, 273.7 292.9, 278.2 290.8"
              fill="none"
              stroke="white"
              strokeWidth={67}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <g transform="matrix(1,0,0,1,-792.488164,-1630.398663)">
          <g transform="matrix(1.174805,0,0,1.174805,791.321901,1628.755793)">
            <path
              className={s.letter}
              fillRule="evenodd"
              d="M68.033,220.466C68.281,218.693 70.084,205.807 72.726,198.584C94.349,139.48 158.126,141.509 177.473,148.575C242.823,172.444 229.945,239.08 226.413,241.334C224.341,242.656 150.011,242.196 117.505,242.644C109.433,242.756 123.05,270.912 152.493,271.645C176.817,272.251 186.812,260.598 190.686,257.789C192.736,256.301 193.981,256.218 215.338,277.656C219.246,281.578 217.6,283.447 212.366,288.355C181.234,317.553 129.931,309.458 109.739,298.05C65.473,273.039 67.69,234.038 68.033,220.466Z M160.558,182.155C166.991,183.77 177.872,186.075 185.194,205.608C187.001,210.429 183.144,209.455 157.5,209.468C116.795,209.49 114.512,210.94 114.685,207.515C114.851,204.22 121.901,190.85 131.744,185.967C143.381,180.193 158.247,181.891 160.558,182.155Z"
            />
          </g>
        </g>
      </g>
    </svg>
  )
}

function LetterH({ maskId }: { maskId: string }) {
  return (
    <svg
      viewBox="480 70 220 300"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={s.letterH}
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="480"
          y="70"
          width="220"
          height="300"
        >
          <rect x="480" y="70" width="220" height="300" fill="black" />
          <g transform="translate(123.9, 63.2) scale(0.79)">
            <path
              className={s.strokeH}
              d="M 507.6 27.3 C 506.4 181.5, 506.8 271.2, 509.5 362.3 C 507.5 219.8, 529.5 242.9, 538.7 193.2 C 570.4 164, 593.1 160.7, 627.7 165.4 C 650.8 177.8, 666.9 200.8, 665.4 243.1 C 664.9 291.9, 663 325, 665.4 370.1"
              fill="none"
              stroke="white"
              strokeWidth={86}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <g transform="matrix(1,0,0,1,-792.488164,-1630.398663)">
          <g transform="matrix(1.174805,0,0,1.174805,791.321901,1628.755793)">
            <path
              className={s.letter}
              d="M577.057,296.484C576.735,303.168 577.306,305.29 572.506,305.606C567.704,305.922 534.748,305.759 533.579,305.25C529.346,303.406 531.762,259.357 531.118,216.516C530.509,176.084 477.931,177.106 473.223,210.462C470.959,226.507 473.8,301.787 471.466,304.476C470.116,306.032 428.826,305.941 427.507,304.495C425.795,302.619 425.845,73.168 426.827,71.673C427.904,70.033 428.354,69.627 468.511,70.099C473.077,70.153 472.571,72.565 472.304,115.499C472.046,157.136 471.343,161.875 475.235,159.08C515.933,129.849 575.94,144.121 577.276,208.503C578.265,256.196 577.268,289.449 577.057,296.484Z"
            />
          </g>
        </g>
      </g>
    </svg>
  )
}

function LetterA({ maskId }: { maskId: string }) {
  return (
    <svg
      viewBox="665 150 250 230"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={s.letterA}
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="665"
          y="150"
          width="250"
          height="230"
        >
          <rect x="665" y="150" width="250" height="230" fill="black" />
          <g transform="translate(183.48, 56.86) scale(0.79)">
            <path
              className={s.strokeA}
              d="M 870.4 159.2 C 872.6 235.4, 867.8 295.4, 871.8 370.3 C 827.9 275.4, 810 358, 775 358 C 727.4 351.5, 698.4 320.6, 692.5 277.8 C 685 225, 714.6 177.8, 770 175 C 810.2 175.3, 822.5 169.4, 844.5 205.7"
              fill="none"
              stroke="white"
              strokeWidth={76}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <g transform="matrix(1,0,0,1,-792.488164,-1630.398663)">
          <g transform="matrix(1.174805,0,0,1.174805,791.321901,1628.755793)">
            <path
              className={s.letter}
              fillRule="evenodd"
              d="M681.471,308.074C678.515,308.089 643.199,313.227 615.992,281.078C582.74,241.784 595.094,152.037 667.477,145.289C707.014,141.603 717.63,167.49 717.951,158.565C718.048,155.869 717.279,148.167 720.561,147.907C721.598,147.824 761.068,147.821 761.438,147.91C764.066,148.546 763.74,149.813 763.847,215.499C763.978,296.503 764.801,296.579 763.321,303.459C762.834,305.724 761.864,305.677 725.5,305.485C711.77,305.413 720.935,296.108 717.478,293.535C714.26,291.141 710.962,304.862 681.471,308.074Z M720.069,224.519C719.671,290.09 629.173,274.2 647.72,211.566C657.698,177.87 714.64,176.887 719.771,220.462C719.929,221.809 719.91,223.173 720.069,224.519Z"
            />
          </g>
        </g>
      </g>
    </svg>
  )
}
